import React, { useState } from "react";
import { Helmet } from "react-helmet";
import AgentDirectory from "@/components/AgentDirectory";
import BrokerageHeader from "@/components/BrokerageHeader";
import DirectoryMarketCards, { marketsFromPlaces } from "@/components/DirectoryMarketCards";
import { useDebounced } from "@/components/AgentDirectorySearch";
import { useBrokerage } from "@/hooks/useAgentDirectory";
import { useManagedBrokerages } from "@/hooks/useBrokerageManage";
import { getLocalStorage, getUserId, getUserType } from "@/helpers/common";
import { USER_INFO_KEY, USER_TYPE_AGENT } from "@/constants/common";
import "./BrokerageView.scss";

export const SEARCH_DELAY_MS = 250;

const hasActiveDemand = (agent) => agent.workingWithBuyers || agent.activeDemand > 0;

export const groupBrokerageAgents = (agents, { searching = false, name = "this brokerage", teams = [] } = {}) => {
  if (searching) return [{ key: "matches", title: "", agents }];
  const onTeam = new Set(teams.flatMap((team) => team.agents.map((agent) => agent.slug)));
  const featured = agents.filter((agent) => agent.featured);
  const rest = agents.filter((agent) => !agent.featured && !onTeam.has(agent.slug));
  const working = rest.filter(hasActiveDemand);
  return [
    { key: "featured", title: "Featured agents", agents: featured },
    ...teams.map((team, index) => ({ key: `team-${index}`, title: team.name, agents: team.agents })),
    {
      key: "working",
      title: "Working with buyers now",
      agents: working,
      emptyNote:
        featured.length || teams.length
          ? ""
          : `No one at ${name} has an active Buyer Want\u2011Listing on Gitcha right\u00a0now.`,
    },
    {
      key: "all",
      title: "All agents",
      agents: rest.filter((agent) => !hasActiveDemand(agent)),
      collapsible: true,
      showLabel: "Show all agents",
      hideLabel: "Hide agents",
    },
  ];
};

const SearchIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d="m13.2 13.2 3.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const BrokerageView = ({ name, network, backLabel, onBack }) => {
  const [text, setText] = useState("");
  const q = useDebounced(text.trim(), SEARCH_DELAY_MS);
  const query = useBrokerage(name, network, q);
  const brokerage = query.data?.pages?.[0]?.brokerage;
  const teams = query.data?.pages?.[0]?.teams || [];
  // Same gate as the Settings > Brokerage tab, so the link never leads to a tab that is not there.
  const signedInAgent =
    Boolean(getUserId()) &&
    Number(getUserType()) === USER_TYPE_AGENT &&
    Boolean(getLocalStorage(USER_INFO_KEY)?.userInfo?.mls);
  const managed = useManagedBrokerages(signedInAgent).data || [];
  // Wait for the page's own network so two same-named brokerages on different MLSs cannot cross.
  const mine = brokerage
    ? managed.find(
        (item) => item.name === name && (!brokerage.network || !item.network || item.network === brokerage.network),
      )
    : null;
  const searching = Boolean(q);

  return (
    <div className="brokerage-view">
      <Helmet>
        <title>{`${name} agents | Gitcha agent directory`}</title>
        <meta name="description" content={`Agents at ${name} and the buyer demand they are working with right now.`} />
      </Helmet>
      <button type="button" className="brokerage-view__back" onClick={onBack}>
        <span aria-hidden="true">←</span> {backLabel}
      </button>

      <BrokerageHeader
        brokerage={brokerage}
        name={name}
        manageHref={mine ? `/settings?tab=brokerage&brokerage=${mine.id}` : null}
      />

      {brokerage?.offices?.places?.length ? (
        <section className="brokerage-view__markets">
          <h2>Where {name} works</h2>
          <DirectoryMarketCards markets={marketsFromPlaces(brokerage.offices.places)} label={`Where ${name} works`} />
        </section>
      ) : null}

      <div className="brokerage-view__agents-head">
        <h2>Agents</h2>
        <div className="brokerage-view__search" role="search">
          <label className="brokerage-view__field">
            <SearchIcon />
            <input
              type="search"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setText("");
              }}
              placeholder={`Agent name at ${name}`}
              aria-label={`Agent name at ${name}`}
              maxLength={60}
              autoComplete="off"
            />
            {text ? (
              <button type="button" className="brokerage-view__clear" onClick={() => setText("")} aria-label="Clear agent name">
                ×
              </button>
            ) : null}
          </label>
        </div>
      </div>

      <AgentDirectory
        query={query}
        scopeLabel={name}
        groupAgents={(agents) => groupBrokerageAgents(agents, { searching, name, teams })}
        hideCompany
        filteredEmpty={
          searching
            ? {
                title: `No agent named “${q}” at ${name}`,
                body: "Check the spelling or try a first or last name on its own. Clear the name to see every agent here.",
                actionLabel: "Show every agent",
                onAction: () => setText(""),
              }
            : null
        }
      />
    </div>
  );
};

export default BrokerageView;
